import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Chip,
  Grow,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { userRoleNameSelector } from '../../redux/modules/user/selectors';
import { useAccessibleFeatures } from './hooks/useAccessibleFeatures';
import { Tab, visibleMenuItemsForTab } from './navigationStructure';

type PopperMenuProps = {
  open: boolean;
  anchorEl: (EventTarget & HTMLElement) | null;
  hoveredTab: Tab | null;
  handleNavigation: (e: React.MouseEvent, pathname: string) => void;
};

const PopperMenu = ({
  open,
  anchorEl,
  hoveredTab,
  handleNavigation,
}: PopperMenuProps) => {
  const intl = useIntl();

  const features = useAccessibleFeatures();
  const role = useSelector(userRoleNameSelector);
  const menuItems = visibleMenuItemsForTab(
    hoveredTab?.menu ?? [],
    features,
    role
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      id="menu-list-grow"
      style={{ zIndex: 12002 }}
    >
      {!!menuItems.length && hoveredTab && (
        <Paper elevation={6} sx={{ borderRadius: borderRadius('s') }}>
          <Grow in={open} appear unmountOnExit key={hoveredTab.name}>
            <List disablePadding sx={{ pt: 1, pb: 1 }}>
              {menuItems.map(menuItem => (
                <ListItem
                  disablePadding
                  dense
                  key={`${hoveredTab.name}-${menuItem.name}`}
                >
                  <ListItemButton
                    component={RouterLink}
                    to={menuItem.link}
                    onClick={e => handleNavigation(e, menuItem.link)}
                    data-cy={`menu-item-${hoveredTab.name}-${menuItem.name}`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <ListItemText
                        primaryTypographyProps={{
                          color: 'text.primary',
                          fontSize: theme => theme.typography.pxToRem(13),
                        }}
                      >
                        {intl.formatMessage({
                          id: `components.appBar.menu.${menuItem.translationKey}`,
                        })}
                      </ListItemText>
                      {menuItem.beta && (
                        <Chip
                          label={intl.formatMessage({
                            id: 'component.betaChip.label',
                          })}
                          size="small"
                          color="warning"
                          sx={{ ml: 2 }}
                        />
                      )}
                      {menuItem.deprecated && (
                        <Chip
                          label={intl.formatMessage({
                            id: 'component.deprecatedChip.label',
                          })}
                          size="small"
                          color="warning"
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Grow>
        </Paper>
      )}
    </Popper>
  );
};

export default PopperMenu;
